import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Home as HomeIcon, Building2, Droplets, Square, Trees, CircleCheck, Phone, Mail, MapPin, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    // Check if user was redirected after successful form submission
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccessMessage(true);
      // Remove the success parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Hide message after 10 seconds
      setTimeout(() => setShowSuccessMessage(false), 10000);
    }
  }, []);

  const testimonials = [
    {
      text: "Prestige Pressure Washing transformed our driveway! It looks brand new. The team was professional, on time, and the results were immediate. Highly recommend!",
      author: "Sarah Mitchell"
    },
    {
      text: "We use Prestige for all our commercial properties. They're reliable, efficient, and always deliver outstanding results. Our storefronts have never looked better.",
      author: "David Thompson"
    },
    {
      text: "The soft wash on our roof was exactly what we needed. They explained the process, were careful with our landscaping, and the results speak for themselves.",
      author: "Jennifer Carlson"
    },
    {
      text: "From quote to completion, everything was seamless. Our deck and patio look amazing. The attention to detail was impressive. Will definitely use them again!",
      author: "Michael Rodriguez"
    },
    {
      text: "Best decision we made for our property! The before and after is incredible. Professional service, fair pricing, and exceptional communication throughout.",
      author: "Amanda Foster"
    },
    {
      text: "Our HOA has been using Prestige for the entire community. Consistent quality, great communication, and they work around our residents' schedules perfectly.",
      author: "Robert Chen"
    }
  ];

  const services = [
    {
      icon: HomeIcon,
      title: 'Residential Pressure Washing',
      description: 'Transform your home\'s exterior with our professional cleaning services. We safely remove dirt, algae, and grime from all surfaces.'
    },
    {
      icon: Building2,
      title: 'Commercial Pressure Washing',
      description: 'Keep your business looking professional with regular exterior maintenance. We service storefronts, offices, and commercial properties.'
    },
    {
      icon: Droplets,
      title: 'Soft Washing',
      description: 'Gentle yet effective cleaning for delicate surfaces like roofs and siding. Our soft wash system eliminates organic growth at the root.'
    },
    {
      icon: Square,
      title: 'Concrete Cleaning',
      description: 'Restore and brighten driveways, sidewalks, and patios. Remove oil stains, rust, and years of buildup for a uniform, clean appearance.'
    },
    {
      icon: Trees,
      title: 'Decks & Patios',
      description: 'Carefully clean and restore wood and composite surfaces. We remove mold and mildew while preserving the integrity of your outdoor spaces.'
    },
    {
      icon: Building2,
      title: 'HOA Communities',
      description: 'Comprehensive cleaning solutions for entire communities. Reliable scheduling and consistent quality for all common areas and facilities.'
    }
  ];

  const benefits = [
    'Locally owned & operated',
    'Residential & commercial expertise',
    'Safe soft-wash techniques',
    'Professional equipment & proven methods',
    'Reliable scheduling & clear communication',
    'Satisfaction-focused service'
  ];

  const serviceTypes = [
    {
      title: 'Home Exteriors',
      description: 'Vinyl, brick, stucco, and Hardie board cleaning that protects your investment and boosts curb appeal instantly.'
    },
    {
      title: 'Driveways & Concrete',
      description: 'Remove stubborn oil stains, restore brightness, and improve safety by eliminating slippery grime buildup.'
    }
  ];

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Success Message Banner */}
      {showSuccessMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top">
          <Card className="bg-[#7FD957] text-white p-6 shadow-2xl border-none max-w-md">
            <div className="flex items-center space-x-4">
              <CircleCheck className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg">Quote Request Received!</h3>
                <p className="text-sm">We'll contact you shortly with your free estimate.</p>
              </div>
              <button 
                onClick={() => setShowSuccessMessage(false)}
                className="ml-4 hover:opacity-80"
              >
                ✕
              </button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_25d41884-eb89-4de0-b8aa-fb9852dff832/artifacts/lmalc9bv_PNG%20TRANSPARENT.png" 
              alt="Prestige Pressure Washing" 
              className="h-16 w-auto"
            />
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#services" className="text-gray-700 hover:text-[#00BFE7] transition-colors font-medium">Services</a>
            <a href="#about" className="text-gray-700 hover:text-[#00BFE7] transition-colors font-medium">About</a>
            <a href="#testimonials" className="text-gray-700 hover:text-[#00BFE7] transition-colors font-medium">Reviews</a>
            <a href="#quote" className="text-gray-700 hover:text-[#00BFE7] transition-colors font-medium">Contact</a>
            <Button className="bg-[#7FD957] hover:bg-[#6BC847] text-white font-semibold px-6">
              <Phone className="w-4 h-4 mr-2" />
              (843) 568-0355
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
          <div className="w-full h-full bg-[#7FD957] transform rotate-12 translate-x-1/4"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <div className="flex items-center space-x-2">
                  <div className="h-1 w-12 bg-[#00BFE7]"></div>
                  <div className="h-1 w-12 bg-[#7FD957]"></div>
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                pressure washing
                <span className="block text-[#00BFE7]">done right</span>
              </h1>
              <div className="h-1 w-32 bg-gradient-to-r from-[#00BFE7] to-[#7FD957]"></div>
              <p className="text-xl text-gray-600 font-medium">Clean. Bright. Impressive.</p>
              <p className="text-lg text-gray-700">Residential & Commercial Exterior Cleaning | Greater Charleston Tri-County Area</p>
              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[#7FD957] text-[#7FD957]" />
                ))}
              </div>
              <div className="pt-4">
                <Button 
                  size="lg" 
                  className="bg-[#00BFE7] hover:bg-[#0099C4] text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                  onClick={() => document.getElementById('quote').scrollIntoView({ behavior: 'smooth' })}
                >
                  REQUEST A FREE QUOTE
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_shine-charleston/artifacts/vbo91vbf_DSC09945.jpg" 
                alt="Professional Pressure Washing" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="bg-[#7FD957] p-3 rounded-full">
                    <CircleCheck className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Since 2010</p>
                    <p className="text-gray-600">Trusted locally</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex justify-center">
              <div className="h-1 w-16 bg-[#7FD957]"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Make Your Property Look <span className="text-[#00BFE7]">Brand New Again</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              At Prestige Pressure Washing, we don't just clean surfaces — we restore curb appeal, protect your investment, and help your property stand out.
            </p>
            <p className="text-lg text-gray-600">
              From stubborn driveway stains to full building washes, we bring the equipment, expertise, and attention to detail to get the job done right — safely and efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="h-1 w-20 bg-gradient-to-r from-[#00BFE7] to-[#7FD957]"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Top-Notch Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide complete pressure washing solutions for both residential and commercial properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-[#00BFE7] group"
                >
                  <div className="bg-gradient-to-br from-[#00BFE7] to-[#7FD957] p-4 rounded-2xl inline-block mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_shine-charleston/artifacts/n9mv529w_DSC09947.jpg" 
                alt="Pressure Washing Services" 
                className="rounded-2xl shadow-2xl w-full h-[600px] object-cover"
              />
            </div>
            <div className="space-y-8">
              <div>
                <div className="h-1 w-16 bg-[#7FD957] mb-6"></div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Guaranteed satisfaction from start to finish.
                </h2>
              </div>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="bg-[#7FD957] rounded-full p-1 mt-1 group-hover:scale-110 transition-transform">
                      <CircleCheck className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-lg text-gray-700 font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Types Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="h-1 w-20 bg-[#7FD957]"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Choose Your Service</h2>
          </div>

          <div className="flex justify-center">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
              {serviceTypes.map((type, index) => (
                <div key={index} className="text-center space-y-4 group">
                  <div className="relative">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2 group-hover:text-[#00BFE7] transition-colors">
                      {type.title}
                    </h3>
                    <div className="h-1 w-16 bg-[#7FD957] mx-auto mb-6 group-hover:w-24 transition-all"></div>
                  </div>
                  <p className="text-gray-600 leading-relaxed px-4">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="h-1 w-16 bg-[#00BFE7] mb-6"></div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Experience You Can Trust</h2>
              <div className="bg-gray-50 p-8 rounded-2xl border-l-4 border-[#7FD957]">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  "Serving the Greater Charleston Tri-County area, we specialize in professional residential and commercial exterior cleaning with results you can see immediately.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  We prioritize our clients' needs and operate with honesty and integrity. Our passion for great customer care has earned us a trusted reputation in the pressure washing industry.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We take pride in every project we undertake, from minor cleaning to full property restoration."
                </p>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="font-bold text-xl text-gray-900">The Prestige Team</p>
                  <p className="text-gray-600">Owners & Operators</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_shine-charleston/artifacts/nahvv4m1_DSC09948.jpg" 
                alt="Professional Team" 
                className="rounded-2xl shadow-2xl w-full h-[600px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#7FD957] p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-white text-white" />
                  ))}
                </div>
                <p className="text-white font-bold mt-2">5-Star Rated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-[#00BFE7] text-[#00BFE7]" />
              ))}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Satisfied Customers</h2>
            <p className="text-xl text-gray-600">Our Google Reviews</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-12 rounded-2xl shadow-xl relative">
              <div className="absolute top-8 left-8 text-[#00BFE7] opacity-20">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="currentColor">
                  <path d="M13.5 27C10.5 27 8 24.5 8 21.5C8 16.5 11 13 16 13C19 13 21.5 15 21.5 18.5C21.5 22 19 24 16 24C15 24 14.5 23.5 14.5 23.5C14.5 27 16.5 29 19.5 29V33C13.5 33 13.5 27 13.5 27ZM33.5 27C30.5 27 28 24.5 28 21.5C28 16.5 31 13 36 13C39 13 41.5 15 41.5 18.5C41.5 22 39 24 36 24C35 24 34.5 23.5 34.5 23.5C34.5 27 36.5 29 39.5 29V33C33.5 33 33.5 27 33.5 27Z" />
                </svg>
              </div>
              <div className="relative z-10 text-center space-y-6">
                <p className="text-xl text-gray-700 leading-relaxed italic">
                  {testimonials[currentTestimonial].text}
                </p>
                <p className="text-lg font-bold text-gray-900">{testimonials[currentTestimonial].author}</p>
              </div>
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button 
                  onClick={handlePrev}
                  className="bg-[#00BFE7] hover:bg-[#0099C4] text-white p-3 rounded-full transition-all hover:scale-110"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <div 
                      key={index}
                      className={`h-2 rounded-full transition-all ${
                        index === currentTestimonial ? 'w-8 bg-[#00BFE7]' : 'w-2 bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <button 
                  onClick={handleNext}
                  className="bg-[#00BFE7] hover:bg-[#0099C4] text-white p-3 rounded-full transition-all hover:scale-110"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Serving Greater Charleston <span className="text-[#00BFE7]">Tri-County</span>
            </h2>
            <p className="text-lg text-gray-700">We proudly serve:</p>
            <div className="grid md:grid-cols-4 gap-6 mt-8">
              {['Charleston', 'North Charleston', 'Mount Pleasant', 'Summerville', 'Goose Creek', 'Ladson', 'Moncks Corner', 'Surrounding Areas'].map((area, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg hover:bg-[#00BFE7] hover:text-white transition-all group">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-[#7FD957] group-hover:text-white" />
                  <p className="font-semibold">{area}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">The Prestige Process</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-[#00BFE7] to-[#7FD957] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Free Estimate', desc: 'Fast, simple quote' },
              { step: '02', title: 'Schedule Service', desc: 'Convenient appointment times' },
              { step: '03', title: 'Professional Cleaning', desc: 'Detailed, safe, efficient' },
              { step: '04', title: 'Final Walkthrough', desc: '100% satisfaction guaranteed' }
            ].map((item, index) => (
              <div key={index} className="text-center space-y-4 group">
                <div className="text-6xl font-bold text-[#00BFE7] opacity-20 group-hover:opacity-100 transition-opacity">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Before Form */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#00BFE7] to-[#7FD957] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            100% Satisfaction from start to finish, no exceptions.
          </h2>
          <div className="h-1 w-32 bg-white mx-auto"></div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">GET A PRICE!</h2>
            <div className="h-1 w-24 bg-[#7FD957] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">Please fill out the form below to schedule a free estimate.</p>
          </div>

          <Card className="p-8 md:p-12 shadow-2xl">
            <form 
              action="https://api.web3forms.com/submit" 
              method="POST"
              className="space-y-6"
            >
              <input type="hidden" name="access_key" value="38be2a1a-c7e3-4e62-85a8-e99e91ba7648" />
              <input type="hidden" name="subject" value="New Quote Request from Prestige Pressure Washing Website" />
              <input type="hidden" name="from_name" value="Prestige Pressure Washing Website" />
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <Input 
                    name="name"
                    required
                    className="border-2 focus:border-[#00BFE7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <Input 
                    name="phone"
                    type="tel"
                    required
                    className="border-2 focus:border-[#00BFE7]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <Input 
                  name="email"
                  type="email"
                  required
                  className="border-2 focus:border-[#00BFE7]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                <Input 
                  name="address"
                  required
                  className="border-2 focus:border-[#00BFE7]"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                  <Input 
                    name="city"
                    required
                    className="border-2 focus:border-[#00BFE7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                  <Input 
                    name="state"
                    required
                    className="border-2 focus:border-[#00BFE7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Zip *</label>
                  <Input 
                    name="zip"
                    required
                    className="border-2 focus:border-[#00BFE7]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Project *</label>
                <select 
                  name="project_type"
                  required
                  className="flex h-10 w-full rounded-md border-2 border-input bg-background px-3 py-2 text-sm ring-offset-background focus:border-[#00BFE7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select project type</option>
                  <option value="Residential Pressure Washing">Residential Pressure Washing</option>
                  <option value="Commercial Pressure Washing">Commercial Pressure Washing</option>
                  <option value="Soft Washing">Soft Washing</option>
                  <option value="Concrete Cleaning">Concrete Cleaning</option>
                  <option value="Deck/Patio Cleaning">Deck/Patio Cleaning</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Project Information</label>
                <Textarea 
                  name="message"
                  rows={4}
                  className="border-2 focus:border-[#00BFE7]"
                  placeholder="Tell us about your project..."
                />
              </div>

              <input type="hidden" name="redirect" value="https://shine-charleston.preview.emergentagent.com/?success=true" />

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-[#00BFE7] hover:bg-[#0099C4] text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all"
              >
                Submit Request
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting your phone number you agree to receive recurring informational messages. Message frequency may vary. Message & data rates may apply.
              </p>
            </form>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_25d41884-eb89-4de0-b8aa-fb9852dff832/artifacts/lmalc9bv_PNG%20TRANSPARENT.png" 
                alt="Prestige Pressure Washing" 
                className="h-20 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-gray-400">Professional exterior cleaning services for the Greater Charleston area.</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#7FD957]">Contact Us</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#00BFE7]" />
                  <span>(843) 568-0355</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#00BFE7]" />
                  <span>prestigepressurewashingsc@gmail.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-[#00BFE7]" />
                  <span>Charleston, SC Tri-County Area</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#7FD957]">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-[#00BFE7] transition-colors cursor-pointer">Residential Pressure Washing</li>
                <li className="hover:text-[#00BFE7] transition-colors cursor-pointer">Commercial Pressure Washing</li>
                <li className="hover:text-[#00BFE7] transition-colors cursor-pointer">Soft Washing</li>
                <li className="hover:text-[#00BFE7] transition-colors cursor-pointer">Concrete Cleaning</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Prestige Pressure Washing. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;